import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import type { ReactElement, ReactNode } from 'react';

import type { ChipData } from './types';

export type OfferRequestCardChip = ChipData & {
    avatar?: string;
    onDelete?: () => void;
};

export type OfferRequestCardDeclineAction = {
    label: string;
    onClick: () => void;
    disabled?: boolean;
};

export type OfferRequestCardProps = {
    title: string;
    subtitle: string;
    chips?: OfferRequestCardChip[];
    onClick?: () => void;
    badge?: string;
    muted?: boolean;
    declineAction?: OfferRequestCardDeclineAction;
    // Rendered opposite the chips, in the bottom-right corner of the card.
    statusChip?: ReactNode;
};

export function OfferRequestCard({
    title,
    subtitle,
    chips = [],
    onClick,
    badge,
    muted = false,
    declineAction,
    statusChip,
}: OfferRequestCardProps): ReactElement {
    return (
        <Card
            variant="outlined"
            sx={{ position: 'relative', ...(muted && { color: 'text.disabled', bgcolor: 'action.hover' }) }}>
            {/* An overlay, so hover and ripple cover the whole card while the decline button stays a sibling
                rather than a button nested in a button. The content above passes clicks through to it. */}
            <CardActionArea
                aria-label={title}
                onClick={onClick}
                disabled={onClick === undefined}
                sx={{ position: 'absolute', inset: 0 }}
            />
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                sx={{ position: 'relative', pointerEvents: 'none' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CardHeader
                        title={title}
                        subheader={subtitle}
                        slotProps={{
                            // A long unbroken work-category name would otherwise widen the whole page on a phone.
                            title: { variant: 'h6', sx: { overflowWrap: 'anywhere' } },
                            subheader: { variant: 'body2', sx: { overflowWrap: 'anywhere' } },
                        }}
                        action={
                            <Stack direction="row" spacing={1} alignItems="center">
                                {badge !== undefined && <Chip label={badge} size="small" />}
                                {!muted && declineAction === undefined && (
                                    <Box
                                        aria-hidden
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'action.active',
                                            p: 0.5,
                                        }}>
                                        <ChevronRightIcon fontSize="small" />
                                    </Box>
                                )}
                            </Stack>
                        }
                    />
                    {(chips.length > 0 || statusChip !== undefined) && (
                        <CardContent sx={{ pt: 0 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                                alignItems="center"
                                justifyContent="space-between">
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {chips.map((chip) => (
                                        <Chip
                                            key={chip.id}
                                            label={chip.label}
                                            size="small"
                                            avatar={chip.avatar ? <Avatar>{chip.avatar}</Avatar> : undefined}
                                            onDelete={chip.onDelete}
                                            sx={chip.onDelete ? { pointerEvents: 'auto' } : undefined}
                                        />
                                    ))}
                                </Stack>
                                {statusChip}
                            </Stack>
                        </CardContent>
                    )}
                </Box>
                {declineAction !== undefined && (
                    <Box
                        sx={{
                            pt: { xs: 0, md: 2 },
                            pb: { xs: 2, md: 0 },
                            // 12px, not CardContent's 16px: the small Button adds ~5px of its own before the icon.
                            pl: { xs: 1.5, md: 0 },
                            pr: 2,
                            flexShrink: 0,
                            alignSelf: 'flex-start',
                            pointerEvents: 'auto',
                        }}>
                        <Button
                            startIcon={<HighlightOffIcon />}
                            color="error"
                            size="small"
                            disabled={declineAction.disabled ?? false}
                            onClick={declineAction.onClick}>
                            {declineAction.label}
                        </Button>
                    </Box>
                )}
            </Stack>
        </Card>
    );
}
